import { keyframes } from '@emotion/react';

const EASE_CURVES = {
  out: [0.23, 1, 0.32, 1],
  inOut: [0.77, 0, 0.175, 1],
  drawer: [0.32, 0.72, 0, 1],
  emphasizedOut: [0.16, 1, 0.3, 1],
} as const satisfies Record<string, readonly [number, number, number, number]>;

const DURATIONS_MS = {
  instant: 120,
  fast: 180,
  base: 240,
  slow: 320,
} as const satisfies Record<string, number>;

const STAGGER_STEP_SEC = 0.04;

export const motion = {
  easeArray: EASE_CURVES,
  ease: {
    out: `cubic-bezier(${EASE_CURVES.out.join(', ')})`,
    inOut: `cubic-bezier(${EASE_CURVES.inOut.join(', ')})`,
    drawer: `cubic-bezier(${EASE_CURVES.drawer.join(', ')})`,
    emphasizedOut: `cubic-bezier(${EASE_CURVES.emphasizedOut.join(', ')})`,
  },
  durationMs: DURATIONS_MS,
  duration: {
    instant: `${DURATIONS_MS.instant}ms`,
    fast: `${DURATIONS_MS.fast}ms`,
    base: `${DURATIONS_MS.base}ms`,
    slow: `${DURATIONS_MS.slow}ms`,
  },
  staggerStepSec: STAGGER_STEP_SEC,
  /**
   * Apple/Linear-style springs for framer-motion `transition`. Springs carry
   * momentum and stay interruptible (a CSS keyframe restarts from zero), which
   * is what makes list reorders and layout shifts feel alive. Keep bounce subtle.
   */
  spring: {
    // Layout/reposition (list reorder, item collapse). Tiny bounce for life.
    snappy: { type: 'spring', duration: 0.35, bounce: 0.18 },
    // Enter/exit where overshoot would look wrong — no bounce.
    smooth: { type: 'spring', duration: 0.4, bounce: 0 },
  },
} as const;

export type MotionTokens = typeof motion;

/**
 * framer-motion variants for a once-only staggered list reveal.
 * Decorative: never blocks interaction. Under `<MotionConfig reducedMotion="user">`
 * the `y` offset is automatically dropped; opacity and the `blur` cue stay (blur
 * is an appearance hint, not a vestibular-motion trigger — framer keeps it like
 * opacity), so the reveal still reads gently for reduced-motion users.
 */
export const revealContainer = {
  hidden: {},
  show: {
    transition: {
      staggerChildren: STAGGER_STEP_SEC,
      delayChildren: 0.02,
    },
  },
} as const;

export const revealItem = {
  hidden: { opacity: 0, y: 8, filter: 'blur(4px)' },
  show: {
    opacity: 1,
    y: 0,
    filter: 'blur(0px)',
    transition: {
      duration: DURATIONS_MS.base / 1000,
      ease: EASE_CURVES.out,
    },
  },
} as const;

/**
 * Shared spinner rotation. One definition reused by every loading spinner
 * (ari-center-button, function-call icon, snackbar ring) instead of redeclaring
 * the @keyframes per component. Period stays per-call — a faster spinner makes
 * loading *feel* faster.
 */
export const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

/**
 * `:active` applies to an element *and its ancestors* while a descendant is
 * pressed, so a container's scale-on-press also fires when a button, toggle,
 * link, or input inside it is clicked — the whole card "bounces" on an inner
 * click. This selector matches the element's own press but NOT presses that
 * bubble up from an interactive descendant, keeping containers still while
 * their controls are used. Use it for clickable *surfaces that contain their
 * own controls* (cards, list rows); leaf buttons should use {@link pressSx}.
 * Requires `:has()` (all evergreen browsers / Chromium ≥105 — fine for both
 * apps' targets).
 */
export const pressActiveSelector =
  '&:active:not(:has(:is(button, a, label, input, select, textarea, summary, [role="button"], [role="switch"], [role="link"], [role="menuitem"], [role="tab"], .MuiButtonBase-root):active))';

/**
 * Press-feedback sx for a leaf clickable element (button, icon button, tab).
 * Subtle scale-down on :active, gated on `useReducedMotion()`. For a clickable
 * container that holds its own controls, scale via {@link pressActiveSelector}
 * instead so it doesn't bounce when an inner control is pressed.
 *
 * Usage:
 *   const reduceMotion = useReducedMotion() ?? false;
 *   <Button sx={pressSx(reduceMotion)}>…</Button>
 */
export function pressSx(reduceMotion: boolean, scale = 0.96) {
  return {
    transition: `transform ${motion.duration.instant} ${motion.ease.out}`,
    '&:active': { transform: reduceMotion ? 'none' : `scale(${scale})` },
  } as const;
}
